import { useState, useMemo, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useAuthStore } from "../service/authStore";
import ConfirmModal from "./ConfirmModal";
import {
  ArrowLeft,
  Download,
  Zap,
  Calendar,
  Clock3,
  Video,
  MapPin,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import "./AddMom.css";

const MOM_API_URL = process.env.REACT_APP_API_URL;

const pointStatusOptions = ["Open", "In Progress", "Closed"];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function todayISO() {
  return new Date().toISOString().split("T")[0];
}

// Captures "now" as HH:MM:SS (24-hour, with seconds) — this is what gets
// sent as actual_m_start_time / actual_m_end_time.
function nowTimeString() {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  const ss = String(d.getSeconds()).padStart(2, "0");
  return `${hh}:${mm}:${ss}`;
}

// Displays a "HH:MM:SS" value as a friendly clock time, e.g. "4:04 PM".
function formatClockTime(timeStr) {
  if (!timeStr) return "";
  const [hh, mm] = timeStr.split(":");
  const d = new Date();
  d.setHours(hh, mm);
  return d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

// Display-only helper: shows a full name in short form, e.g.
// "Abhishek Pachpol" -> "Abhishek P". Only affects what's rendered in chips
// and suggestion dropdowns — the full name is still what's stored/sent.
function shortenName(fullName) {
  if (!fullName) return "";
  const parts = fullName.trim().split(/\s+/);
  if (parts.length <= 1) return parts[0] || "";
  return `${parts[0]} ${parts[1].charAt(0).toUpperCase()}`;
}

// Resolves a typed name against the active user list.
// - If the name matches an active user (case-insensitive), that user's
//   id/email travel along with the name (e.g. "Abhishek", "Piyush").
// - If there's no match, only the name is kept — user_id and email are
//   left null (e.g. "Sahil", who isn't in the user list).
function matchUserByName(rawName, userList) {
  const name = (rawName || "").trim();
  if (!name) return null;

  const found = (userList || []).find(
    (u) => (u.name || u.full_name || "").toLowerCase() === name.toLowerCase(),
  );

  if (found) {
    return {
      name: found.name || found.full_name,
      user_id: found.id ?? found.user_id ?? null,
      email: found.email || null,
    };
  }

  return { name, user_id: null, email: null };
}

function emptyPoint() {
  return {
    id: 0, // 0 => new row, insert on the backend
    point_discussed: "",
    responsible_name: "",
    responsible_user_id: null,
    responsible_email: null,
    target_date: "",
    status: "",
    remarks: "",
  };
}

// ---------------------------------------------------------------------------
// Textarea-like growing field, without an actual <textarea> element.
// A contentEditable div that wraps text and grows with it (up to a max
// height, then scrolls), styled to match the other table inputs.
// ---------------------------------------------------------------------------

function GrowingTextInput({
  value,
  onChange,
  placeholder,
  onKeyDown,
  disabled,
  innerRef,
}) {
  const divRef = useRef(null);

  // Only push external value into the DOM when it actually differs from
  // what's currently shown — otherwise every keystroke (which already
  // updates `value` via onChange) would reset the cursor position.
  useEffect(() => {
    if (divRef.current && divRef.current.innerText !== (value || "")) {
      divRef.current.innerText = value || "";
    }
  }, [value]);

  return (
    <div
      ref={(el) => {
        divRef.current = el;
        if (innerRef) innerRef.current = el;
      }}
      className="growing-text-input"
      contentEditable={!disabled}
      suppressContentEditableWarning
      data-placeholder={placeholder}
      onInput={(e) => onChange(e.currentTarget.innerText)}
      onPaste={(e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        document.execCommand("insertText", false, text);
      }}
      onKeyDown={onKeyDown}
    />
  );
}

// ---------------------------------------------------------------------------
// Single-person autocomplete (Organised By / MOM Prepared By / Responsibility)
// ---------------------------------------------------------------------------
//
// Behavior: the person can either pick a name from the active user list, or
// type a name that isn't in the list — free text is accepted as-is (unless
// restrictToList is set, e.g. for MOM Prepared By, which must resolve to a
// real user). Either way there is only ever a single value held here.
//
// Display: once a value is committed and the field isn't focused, the input
// shows the short form ("Abhishek P") like everywhere else. While the person
// is actively typing/editing, it shows the full text so they can see exactly
// what they're changing.

function PersonAutocomplete({
  users,
  value,
  onChange,
  placeholder,
  compact,
  restrictToList,
  hasError,
}) {
  const [inputValue, setInputValue] = useState(value?.name || "");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const wrapperRef = useRef(null);

  useEffect(() => {
    setInputValue(value?.name || "");
  }, [value]);

  // The dropdown is rendered in a portal (see below) so that the points
  // table's horizontally-scrollable wrapper can't clip it vertically —
  // overflow-x: auto on that wrapper forces overflow-y to clip too, which
  // was cutting the suggestion list off and forcing an inner scrollbar.
  // Since it's portaled to <body>, its position has to be computed here
  // instead of via CSS.
  const updateMenuPos = () => {
    if (!wrapperRef.current) return;
    const rect = wrapperRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, left: rect.left, width: rect.width });
  };

  useEffect(() => {
    if (!showSuggestions) return;
    updateMenuPos();
    const handle = () => updateMenuPos();
    window.addEventListener("scroll", handle, true);
    window.addEventListener("resize", handle);
    return () => {
      window.removeEventListener("scroll", handle, true);
      window.removeEventListener("resize", handle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showSuggestions]);

  const suggestions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return [];
    return (users || [])
      .filter((u) => (u.name || u.full_name || "").toLowerCase().includes(q))
      .slice(0, 6);
  }, [inputValue, users]);

  const selectUser = (u) => {
    const val = {
      name: u.name || u.full_name,
      user_id: u.id ?? u.user_id,
      email: u.email || null,
    };
    setInputValue(val.name);
    onChange(val);
    setShowSuggestions(false);
  };

  const commitFreeText = () => {
    setShowSuggestions(false);
    const trimmed = inputValue.trim();

    if (!trimmed) {
      onChange(null);
      return;
    }

    const matched = matchUserByName(trimmed, users);

    // restrictToList fields (e.g. MOM Prepared By) must resolve to an
    // active user — if there's no match, reject the typed text and
    // fall back to the last confirmed value instead of accepting a guest.
    if (restrictToList && !matched.user_id) {
      setInputValue(value?.name || "");
      return;
    }

    onChange(matched);
  };

  // Full name while editing, short name once committed and blurred.
  const displayValue = isFocused
    ? inputValue
    : value?.name
      ? shortenName(value.name)
      : inputValue;

  return (
    <div
      className={`person-picker ${compact ? "person-picker-compact" : ""} ${hasError ? "person-picker-error" : ""}`}
      ref={wrapperRef}
    >
      <input
        type="text"
        placeholder={placeholder}
        value={displayValue}
        title={value?.name || ""}
        className={hasError ? "input-error" : ""}
        onChange={(e) => {
          setInputValue(e.target.value);
          setShowSuggestions(true);
        }}
        onFocus={() => {
          setIsFocused(true);
          setShowSuggestions(true);
        }}
        onBlur={() => {
          setIsFocused(false);
          commitFreeText();
        }}
      />
      {showSuggestions &&
        suggestions.length > 0 &&
        menuPos &&
        createPortal(
          <div
            className="suggestions-dropdown-portal"
            style={{
              top: menuPos.top,
              left: menuPos.left,
              width: menuPos.width,
            }}
          >
            {suggestions.map((u) => (
              <div
                key={u.id ?? u.user_id}
                className="suggestion-item"
                title={u.name}
                onMouseDown={() => selectUser(u)}
              >
                {u.name} | {u.department_name} | {u.location_name}
              </div>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Multi-person picker (Attended By)
// ---------------------------------------------------------------------------

function AttendeeMultiSelect({ users, selected, onChange,hasError }) {
  const [inputValue, setInputValue] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapperRef = useRef(null);

  const suggestions = useMemo(() => {
    const q = inputValue.trim().toLowerCase();
    if (!q) return [];
    return (users || [])
      .filter((u) => {
        const uname = (u.name || u.full_name || "").toLowerCase();
        const uid = u.id ?? u.user_id;
        const alreadyAdded = selected.some(
          (s) => s.user_id != null && s.user_id === uid,
        );
        return uname.includes(q) && !alreadyAdded;
      })
      .slice(0, 6);
  }, [inputValue, users, selected]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const addPerson = (person) => {
    const alreadyAdded = selected.some(
      (s) => s.name.toLowerCase() === person.name.toLowerCase(),
    );
    if (!alreadyAdded) onChange([...selected, person]);
    setInputValue("");
    setShowSuggestions(false);
  };

  const addFromUser = (u) =>
    addPerson({
      id: 0,
      name: u.name || u.full_name,
      user_id: u.id ?? u.user_id,
      email: u.email || null,
    });

  const addFreeText = () => {
    const name = inputValue.trim();
    if (!name) return;
    addPerson({ id: 0, ...matchUserByName(name, users) });
  };

  const removeAt = (idx) => onChange(selected.filter((_, i) => i !== idx));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (suggestions.length > 0) addFromUser(suggestions[0]);
      else addFreeText();
    } else if (e.key === "Backspace" && !inputValue && selected.length > 0) {
      removeAt(selected.length - 1);
    }
  };

  return (
    <div className={`attendee-picker ${hasError ? "input-error" : ""}`} ref={wrapperRef}>
      <div className="attendee-chip-row">
        {selected.map((s, idx) => (
          <span
            key={`${s.name}-${idx}`}
            className={`chip ${s.user_id ? "chip-known" : "chip-unknown"}`}
            title={
              s.user_id
                ? s.name + (s.email ? ` (${s.email})` : "")
                : `${s.name} (not in user list)`
            }
          >
            {shortenName(s.name)}
            <X
              size={13}
              className="chip-remove"
              onClick={() => removeAt(idx)}
            />
          </span>
        ))}
        <input
          type="text"
          placeholder={
            selected.length === 0 ? "Start typing a name" : "Add another..."
          }
          value={inputValue}
          onChange={(e) => {
            setInputValue(e.target.value);
            setShowSuggestions(true);
          }}
          onFocus={() => setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
        />
      </div>

      {showSuggestions && inputValue.trim() && (
        <div className="suggestions-dropdown">
          {suggestions.length > 0 ? (
            suggestions.map((u) => (
              <div
                key={u.id ?? u.user_id}
                className="suggestion-item"
                title={u.name}
                onMouseDown={() => addFromUser(u)}
              >
                {u.name} | {u.department_name} | {u.location_name}
              </div>
            ))
          ) : (
            <div
              className="suggestion-item suggestion-freetext"
              onMouseDown={addFreeText}
            >
              Add "{inputValue.trim()}" as guest (not in user list)
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function AddMom({ onClose, onCreated }) {
  const navigate = useNavigate();
  const users = useAuthStore((state) => state.users);
  const token = useAuthStore((state) => state.token);
  const user = useAuthStore((state) => state.user);
  const companyId = useAuthStore((state) => state.companyId);

  // Header -----------------------------------------------------------------
  const [title, setTitle] = useState("");
  const titleInputRef = useRef(null);
  const meetingDateRef = useRef(null);
  const organizedByRef = useRef(null);
  const preparedByRef = useRef(null);

  // ============================================================================
  // VALIDATION STATE - Track which fields have errors
  // ============================================================================
  const [errors, setErrors] = useState({});

  // ============================================================================
  // NEW: Auto-focus title input when component mounts
  // ============================================================================
  useEffect(() => {
    if (titleInputRef.current) {
      titleInputRef.current.focus();
      titleInputRef.current.select();
    }
  }, []);

  const [scheduleMode, setScheduleMode] = useState("instant"); // "instant" | "scheduled"
  // meeting_type is derived from the Instant/Schedule choice rather than
  // being a separate field.
  const meetingType = scheduleMode === "instant" ? "Instant" : "Scheduled";
  const [meetingDate, setMeetingDate] = useState(todayISO());
  const [startTime, setStartTime] = useState("");
  const [endTime, setEndTime] = useState("");

  const [organizedBy, setOrganizedBy] = useState(null);
  const [preparedBy, setPreparedBy] = useState(null);

  const [purpose, setPurpose] = useState("");

  const [meetingMode, setMeetingMode] = useState("Online"); // "Online" | "In-Person"
  const [location, setLocation] = useState("");
  const [meetingLink, setMeetingLink] = useState("");

  const [attendees, setAttendees] = useState([]);
  const [discussionPoints, setDiscussionPoints] = useState([
    emptyPoint(),
    emptyPoint(),
  ]);

  // Actual meeting start/end — only relevant for Instant meetings, captured
  // via the Start Meeting / End Meeting buttons rather than typed in.
  const [actualStartTime, setActualStartTime] = useState("");
  const [actualEndTime, setActualEndTime] = useState("");

  const [saving, setSaving] = useState(false);
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);

  const lastRemarkRef = useRef(null);

  const handleStartTimeChange = (e) => {
    const value = e.target.value;
    setStartTime(value);

    if (!value) {
      setEndTime("");
      return;
    }

    // Calculate +1 hour
    const [hours, minutes] = value.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    date.setHours(date.getHours() + 1);

    const defaultEndTime =
      String(date.getHours()).padStart(2, "0") +
      ":" +
      String(date.getMinutes()).padStart(2, "0");

    // Set default end time only if:
    // - end time is empty
    // - or current end time is before the new start time
    if (!endTime || endTime < value) {
      setEndTime(defaultEndTime);
    }
  };

  const handleEndTimeChange = (e) => {
    const value = e.target.value;

    if (startTime && value < startTime) {
      toast.error("End time cannot be earlier than Start time");
      return;
    }

    setEndTime(value);
  };

  // MOM Prepared By defaults to the logged-in user, still editable.
  useEffect(() => {
    if (user && !preparedBy) {
      const name = user.name || user.full_name;
      if (name) {
        setPreparedBy({
          name,
          user_id: user.user_id ?? user.id ?? null,
          email: user.email ?? null,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (scheduleMode === "instant") setMeetingDate(todayISO());
  }, [scheduleMode]);

  // Discussion points --------------------------------------------------------
  const updatePoint = (idx, patch) => {
    setDiscussionPoints((prev) =>
      prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    );
  };

  const addPointRow = () =>
    setDiscussionPoints((prev) => [...prev, emptyPoint()]);

  const removePointRow = (idx) =>
    setDiscussionPoints((prev) => prev.filter((_, i) => i !== idx));

  // Enter in the last row's Remarks field adds a new point row, rather
  // than inserting a line break — wrapping still happens automatically
  // when a line runs out of width.
  const handleRemarksKeyDown = (idx, e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      if (idx === discussionPoints.length - 1) addPointRow();
    }
  };

  // Enter in Points Discussed doesn't do anything special (matches the
  // old plain-input behavior) — text still wraps to the next line on its
  // own once it runs out of width.
  const handlePointDiscussedKeyDown = (e) => {
    // Allow Shift + Enter to create a new line
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
    }
  };

  // Save / Publish -------------------------------------------------------------
  const buildPayload = (status) => ({
    meeting_title: title.trim(),
    meeting_type: meetingType,
    purpose: purpose.trim() || null,
    meeting_date: meetingDate,
    start_time: startTime || null,
    end_time: endTime || null,
    actual_m_start_time: actualStartTime || null,
    actual_m_end_time: actualEndTime || null,
    location: meetingMode === "In-Person" ? location.trim() || null : null,
    meeting_mode: meetingMode,
    meeting_link: meetingMode === "Online" ? meetingLink.trim() || null : null,
    organized_by_user_id: organizedBy?.user_id ?? null,
    organized_by_name: organizedBy?.name ?? null,
    organized_by_email: organizedBy?.email ?? null,
    prepared_by_user_id: preparedBy?.user_id ?? null,
    prepared_by_name: preparedBy?.name ?? null,
    prepared_by_email: preparedBy?.email ?? null,
    status,
    company_id: companyId,
    created_by: user?.user_id ?? user?.id ?? null,
    created_by_email: user?.emailid ?? null,
    created_by_name: user?.username ?? null,

    attendance: attendees.map((a) => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const isEmail = emailRegex.test((a.name || "").trim());

      return {
        id: a.id ?? 0,
        attended_user_id: a.user_id ?? null,

        // Keep whatever the user entered
        attended_employee_name: a.name,

        // If it's already an email, copy it to attended_email as well
        attended_email: a.email ?? (isEmail ? a.name.trim() : null),

        attendance_status: "Present",
      };
    }),

    discussion_points: discussionPoints
      .filter((p) => p.point_discussed.trim())
      .map((p) => ({
        id: p.id ?? 0,
        point_discussed: p.point_discussed.trim(),
        responsible_user_id: p.responsible_user_id ?? null,
        responsible_name: p.responsible_name || null,
        responsible_email: p.responsible_email ?? null,
        target_date: p.target_date || null,
        status: p.status,
        remarks: p.remarks || null,
      })),
  });

  // ============================================================================
  // UPDATED: validate() function returns errors object
  // ============================================================================
  const validate = (status) => {
    const newErrors = {};

    // Check title
    if (!title.trim()) {
      newErrors.title = true;
    }

    // Check meeting date
    if (!meetingDate) {
      newErrors.meetingDate = true;
    }

    // Check meeting mode
    if (!meetingMode) {
      newErrors.meetingMode = true;
    }

    // Check Note Taker (preparedBy)
    if (!preparedBy) {
      newErrors.preparedBy = true;
    }

    // Check Organised By
    if (!organizedBy) {
      newErrors.organizedBy = true;
    }

    // Check minimum 2 attendees
    if (attendees.length < 2) {
      newErrors.attendees = true;
    }

    // Draft -> only Start/End Time mandatory
    if (status === "Draft") {
      if (!startTime) {
        newErrors.startTime = true;
      }

      if (!endTime) {
        newErrors.endTime = true;
      }
    }

    // End time validation
    if (startTime && endTime && endTime < startTime) {
      newErrors.endTime = true;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? null : newErrors;
  };

  // ============================================================================
  // UPDATED: handleSave() with validation errors and auto-focus
  // ============================================================================
  const handleSave = async (status) => {
    const validationErrors = validate(status);

    // If there are errors, scroll to first error field and focus it
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      if (validationErrors.title && titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (validationErrors.meetingDate && meetingDateRef.current) {
        meetingDateRef.current.focus();
        meetingDateRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (validationErrors.startTime) {
        document.querySelector('input[type="time"]')?.focus();
      } else if (validationErrors.organizedBy && organizedByRef.current) {
        organizedByRef.current.focus();
        organizedByRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (validationErrors.preparedBy && preparedByRef.current) {
        preparedByRef.current.focus();
        preparedByRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      

      toast.error("Please fill in all required fields");
      return;
    }

    if (status === "Completed") {
      if (!actualStartTime) {
        toast.error("Please click Start Meeting before publishing.");
        return;
      }

      if (!actualEndTime) {
        toast.error("Please click End Meeting before publishing.");
        return;
      }
    }

    if (!token || !companyId) {
      toast.error("Missing session — please log in again");
      return;
    }

    setSaving(true);

    try {
      const res = await axios.post(
        `${MOM_API_URL}/mom/${companyId}`,
        buildPayload(status),
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(
        status === "Draft" ? "Saved as draft" : "MOM published successfully",
      );
      setErrors({}); // Clear errors on success
      if (onCreated) onCreated(res.data);
      else navigate("/mom");
    } catch (err) {
      console.error("Failed to create MOM:", err);
      toast.error("Failed to save meeting. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePublishClick = () => {
    const validationErrors = validate("Completed");

    if (validationErrors && Object.keys(validationErrors).length > 0) {
      if (validationErrors.title && titleInputRef.current) {
        titleInputRef.current.focus();
        titleInputRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (validationErrors.meetingDate && meetingDateRef.current) {
        meetingDateRef.current.focus();
        meetingDateRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (validationErrors.startTime) {
        document.querySelector('input[type="time"]')?.focus();
      } else if (validationErrors.organizedBy && organizedByRef.current) {
        organizedByRef.current.focus();
        organizedByRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      } else if (validationErrors.preparedBy && preparedByRef.current) {
        preparedByRef.current.focus();
        preparedByRef.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }

      toast.error("Please fill in all required fields");
      return;
    }

    setShowPublishConfirm(true);
  };

  const handleConfirmPublish = () => {
    setShowPublishConfirm(false);
    handleSave("Completed");
  };

  return (
    <div className="add-mom-page">
      <div className="add-mom-header">
        <button
          className="btn-icon"
          onClick={() => (onClose ? onClose() : navigate("/mom"))}
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>

        <div className="add-mom-title-block">
          <input
            ref={titleInputRef}
            className={`title-input ${errors.title ? "input-error" : ""}`}
            placeholder="Enter Meeting Title"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              if (errors.title) setErrors({ ...errors, title: false });
            }}
          />
        </div>
      </div>

      <div className="add-mom-scroll-area">
        <div className="form-row">
          <div className="form-block form-block-narrow">
            <span className="form-label">Day & Date</span>
            <div className="segmented-toggle">
              <button
                className={scheduleMode === "instant" ? "active" : ""}
                onClick={() => setScheduleMode("instant")}
              >
                <Zap size={14} /> Instant
              </button>
              <button
                className={scheduleMode === "scheduled" ? "active" : ""}
                onClick={() => setScheduleMode("scheduled")}
              >
                <Calendar size={14} /> Schedule
              </button>
            </div>
            {scheduleMode === "instant" ? (
              <span className="static-date">
                {new Date(meetingDate).toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric",
                })}
              </span>
            ) : (
              <input
                ref={meetingDateRef}
                type="date"
                className={`date-input ${errors.meetingDate ? "input-error" : ""}`}
                value={meetingDate}
                onChange={(e) => {
                  setMeetingDate(e.target.value);
                  if (errors.meetingDate)
                    setErrors({ ...errors, meetingDate: false });
                }}
              />
            )}
          </div>

          <div className="form-block form-block-time">
            <span className="form-label">Start Time</span>
            <div className="time-input-wrap">
              <Clock3 size={14} className="time-icon" />
              <input
                type="time"
                className={errors.startTime ? "input-error" : ""}
                value={startTime}
                onChange={(e) => {
                  handleStartTimeChange(e);

                  if (errors.startTime) {
                    setErrors((prev) => ({
                      ...prev,
                      startTime: false,
                    }));
                  }
                }}
              />
            </div>
            {scheduleMode === "instant" && (
              <button
                type="button"
                className="btn-meeting-action btn-meeting-start"
                onClick={() => {
                  const current = nowTimeString();

                  setActualStartTime(current);

                  // Auto fill Start Time
                  setStartTime(current.substring(0, 5));

                  if (errors.startTime) {
                    setErrors((prev) => ({
                      ...prev,
                      startTime: false,
                    }));
                  }
                }}
                disabled={!!actualStartTime}
              >
                {actualStartTime
                  ? `Started at ${formatClockTime(actualStartTime)}`
                  : "Start Meeting"}
              </button>
            )}
          </div>

          <div className="form-block form-block-time">
            <span className="form-label">End Time</span>
            <div className="time-input-wrap">
              <Clock3 size={14} className="time-icon" />
              <input
                type="time"
                className={errors.endTime ? "input-error" : ""}
                value={endTime}
                min={startTime}
                onChange={(e) => {
                  handleEndTimeChange(e);

                  if (errors.endTime) {
                    setErrors((prev) => ({
                      ...prev,
                      endTime: false,
                    }));
                  }
                }}
              />
            </div>
            {scheduleMode === "instant" && (
              <button
                type="button"
                className="btn-meeting-action btn-meeting-end"
                onClick={() => {
                  const current = nowTimeString();

                  setActualEndTime(current);

                  // Auto fill End Time
                  setEndTime(current.substring(0, 5));

                  if (errors.endTime) {
                    setErrors((prev) => ({
                      ...prev,
                      endTime: false,
                    }));
                  }
                }}
                disabled={!actualStartTime || !!actualEndTime}
              >
                {actualEndTime
                  ? `Ended at ${formatClockTime(actualEndTime)}`
                  : "End Meeting"}
              </button>
            )}
          </div>

          <div className="form-block">
            <span className="form-label">Organised By</span>
            <PersonAutocomplete
              ref={organizedByRef}
              users={users}
              value={organizedBy}
              onChange={(val) => {
                setOrganizedBy(val);
                if (errors.organizedBy)
                  setErrors({ ...errors, organizedBy: false });
              }}
              placeholder="Search or enter a name"
              hasError={errors.organizedBy}
            />
          </div>

          <div className="form-block">
            <span className="form-label">Note Taker</span>
            <PersonAutocomplete
              ref={preparedByRef}
              users={users}
              value={preparedBy}
              onChange={(val) => {
                setPreparedBy(val);
                if (errors.preparedBy)
                  setErrors({ ...errors, preparedBy: false });
              }}
              placeholder="Search name"
              restrictToList
              hasError={errors.preparedBy}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-block form-block-wide">
            <span className="form-label">Purpose</span>
            <input
              type="text"
              className="text-input"
              placeholder="Enter Purpose of the meeting"
              value={purpose}
              onChange={(e) => setPurpose(e.target.value)}
            />
          </div>

          <div className="form-block form-block-wide">
            <span className="form-label">Meeting mode</span>
            <div className="location-row">
              <div className="segmented-toggle">
                <button
                  className={meetingMode === "Online" ? "active" : ""}
                  onClick={() => setMeetingMode("Online")}
                >
                  <Video size={14} /> Online
                </button>
                <button
                  className={meetingMode === "In-Person" ? "active" : ""}
                  onClick={() => setMeetingMode("In-Person")}
                >
                  <MapPin size={14} /> In-Person
                </button>
              </div>
              {meetingMode === "Online" ? (
                <input
                  type="text"
                  className="text-input"
                  placeholder="Enter meeting link (Teams/Zoom/Meet)"
                  value={meetingLink}
                  onChange={(e) => setMeetingLink(e.target.value)}
                />
              ) : (
                <input
                  type="text"
                  className="text-input"
                  placeholder="Enter venue / address"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              )}
            </div>
          </div>
        </div>

        <div className="form-row">
          <div className="form-block form-block-full">
            <span className="form-label">Attendees</span>
           <AttendeeMultiSelect
  users={users}
  selected={attendees}
  onChange={(value) => {
    setAttendees(value);

    // Remove red border once minimum 2 attendees are added
    if (value.length >= 2 && errors.attendees) {
      setErrors((prev) => ({
        ...prev,
        attendees: false,
      }));
    }
  }}
  hasError={errors.attendees}
/>
          </div>
        </div>

        <div className="detail-sectionchild">
          <h4 className="section-title-child">
            <span className="dot" /> Discussion summary
          </h4>

          <div className="points-table-wrapper">
            <table className="points-table editable">
              <thead>
                <tr>
                  <th>Sr.</th>
                  <th>Points Discussed</th>
                  <th>PIC</th>
                  <th>Target Date</th>
                  <th>Status</th>
                  <th>Remarks</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {discussionPoints.map((point, idx) => (
                  <tr key={idx}>
                    <td>{idx + 1}</td>
                    <td>
                      <GrowingTextInput
                        placeholder="What was discussed/decided"
                        value={point.point_discussed}
                        onChange={(text) =>
                          updatePoint(idx, { point_discussed: text })
                        }
                        onKeyDown={handlePointDiscussedKeyDown}
                      />
                    </td>
                    <td>
                      <PersonAutocomplete
                        compact
                        users={users}
                        value={
                          point.responsible_name
                            ? {
                                name: point.responsible_name,
                                user_id: point.responsible_user_id,
                                email: point.responsible_email,
                              }
                            : null
                        }
                        onChange={(val) =>
                          updatePoint(idx, {
                            responsible_name: val?.name || "",
                            responsible_user_id: val?.user_id ?? null,
                            responsible_email: val?.email ?? null,
                          })
                        }
                        placeholder="Name"
                      />
                    </td>
                    <td>
                      <input
                        type="date"
                        value={point.target_date}
                        onChange={(e) =>
                          updatePoint(idx, { target_date: e.target.value })
                        }
                      />
                    </td>
                    <td>
                      <select
                        value={point.status || ""}
                        onChange={(e) =>
                          updatePoint(idx, { status: e.target.value })
                        }
                      >
                        <option value="">Select Status</option>
                        {pointStatusOptions.map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <GrowingTextInput
                        placeholder="Remark/Dependency"
                        value={point.remarks}
                        onChange={(text) => updatePoint(idx, { remarks: text })}
                        onKeyDown={(e) => handleRemarksKeyDown(idx, e)}
                        innerRef={
                          idx === discussionPoints.length - 1
                            ? lastRemarkRef
                            : null
                        }
                      />
                    </td>
                    <td>
                      <button
                        className="btn-delete-row"
                        onClick={() => removePointRow(idx)}
                        aria-label="Delete point"
                      >
                        <Trash2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <button className="btn-add-point" onClick={addPointRow}>
            <Plus size={14} /> Add point
          </button>
        </div>
      </div>

      <div className="add-mom-footer">
        <button
          className="btn-draft"
          disabled={saving}
          onClick={() => handleSave("Draft")}
        >
          Save
        </button>
        <button
          className="btn-publish"
          disabled={saving}
          onClick={handlePublishClick}
        >
          {saving ? "Saving..." : "Publish MOM"}
        </button>
      </div>

      <ConfirmModal
        open={showPublishConfirm}
        title="Publish this MOM?"
        message="Once published, this MOM is locked — you won't be able to amend it or add any new points. Make sure everything discussed is captured before you proceed."
        confirmLabel="Yes, Publish"
        cancelLabel="No"
        busy={saving}
        onConfirm={handleConfirmPublish}
        onCancel={() => setShowPublishConfirm(false)}
      />
    </div>
  );
}

export default AddMom;
